import { create } from "zustand";
import { registerGlobalStoreReset } from "../../state/resetRegistry";

export const paidCreationPaths = {
  "machine.create": "/machine/create",
  "location.create": "/location/create",
  "expense.create": "/expense",
} as const;

export type SubscriptionRequirement = {
  code: "subscription_required";
  action: keyof typeof paidCreationPaths;
  actionLabel: string;
  organizationId: string;
  operationId: string;
};

let pending: { settle: (confirmed: boolean) => void } | null = null;

type BillingActionModalState = {
  open: boolean;
  title: string;
  ownerMessage: string;
  memberMessage: string;
  requirement: SubscriptionRequirement | null;
  requestSubscription: (requirement: SubscriptionRequirement, signal?: AbortSignal | null) => Promise<boolean>;
  confirmSubscription: (operationId: string) => void;
  showActivationFailure: () => void;
  close: () => void;
};

export const useBillingActionModalStore = create<BillingActionModalState>((set, get) => ({
  open: false,
  title: "Machine Activated, Payment Failed",
  ownerMessage: "Update billing to retry the outstanding activation charge.",
  memberMessage: "The machine was activated, but the owner's payment method needs to be updated before the activation charge can be retried.",
  requirement: null,
  requestSubscription: (requirement, signal) => {
    // Only one interrupted save can own the upgrade dialog.
    if (pending || signal?.aborted) return Promise.resolve(false);
    return new Promise<boolean>(resolve => {
      const abort = () => get().close();
      pending = { settle: confirmed => {
        signal?.removeEventListener("abort", abort);
        pending = null;
        set({ open: false, requirement: null });
        resolve(confirmed);
      } };
      signal?.addEventListener("abort", abort, { once: true });
      set({ open: true, title: `Subscribe to ${requirement.actionLabel.toLowerCase()}`,
        requirement, ownerMessage: "Your form will stay open while you review the subscription.",
        memberMessage: "Ask your organization owner to subscribe. Your form will stay open." });
    });
  },
  confirmSubscription: operationId => {
    if (get().requirement?.operationId === operationId) pending?.settle(true);
  },
  showActivationFailure: () => {
    if (pending) return;
    set({ open: true, requirement: null, title: "Machine Activated, Payment Failed",
      ownerMessage: "Update billing to retry the outstanding activation charge.",
      memberMessage: "The machine was activated, but the owner's payment method needs to be updated before the activation charge can be retried." });
  },
  close: () => {
    if (pending) pending.settle(false);
    else set({ open: false, requirement: null });
  },
}));

registerGlobalStoreReset(() => useBillingActionModalStore.getState().close());
